import { getPortalBaseUrl } from '../js/constants/constants';
import { VIEWER_MESSAGE } from '../library/types/ViewerMessage';
import { CreateJobPayload, JobFileset, Job, JobListRequest, JobFilesetsRequest } from '../types/Job';
import { ListCollection, PagingRequest } from '../types/List';
import { infoSubject$ } from '../utilities/info-observable';
import { fetchData } from './fetch-service';
import { getAccessToken } from './token.service';

const PortalBaseUrl = getPortalBaseUrl();

export const FILTERS = {
    ALL: 'All',
    RUNNING: 'Running',
    COMPLETED: 'Completed',
    READY_FOR_DOWNLOAD: 'Ready for Download',
    FAILED: 'Failed'
};

/** Job types that produce a downloadable zip and are surfaced in the "Ready for Download" tab. */
export const DOWNLOAD_JOB_TYPES = ['DownloadFiles', 'DownloadFolder', 'Crop_DownloadFile'];

export type Filters = (typeof FILTERS)[keyof typeof FILTERS];

export const JOBTYPE = {
    MYJOBS: 'My Jobs',
    ALLJOBS: 'All Jobs'
};

export type JobType = (typeof JOBTYPE)[keyof typeof JOBTYPE];

export const getJob = async (key: string) => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/jobs/${key}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    return response.json();
};

export const createJob = async (body: CreateJobPayload): Promise<string> => {
    const accessToken = await getAccessToken();

    const response = await fetchData(`${PortalBaseUrl}/api/jobs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        if (response.status === 400) {
            const responseJson = await response.json();
            const errorText = responseJson.errors[0].message;
            infoSubject$.next({
                text: errorText,
                variant: VIEWER_MESSAGE.WARNING,
                timeout: 5000
            });
        } else {
            infoSubject$.next({
                text: 'There was an error in creating your job',
                variant: VIEWER_MESSAGE.WARNING,
                timeout: 5000
            });
        }

        return Promise.reject(response);
    }

    const { key } = await response.json();

    return key;
};

export const getJobFilesets = async (key: string, options: JobFilesetsRequest): Promise<ListCollection<JobFileset>> => {
    const accessToken = await getAccessToken();

    const response = await fetchData(`${PortalBaseUrl}/api/jobs/${key}/filesets?page=${options.page}&perpage=${options.perpage}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};

export const updateFileSetProgress = async (
    key: string,
    jobFileSetKey: string,
    segmentSizeUploaded: number,
    eTag: string,
    isCompleted?: boolean
): Promise<string> => {
    const accessToken = await getAccessToken();

    const response = await fetch(`${PortalBaseUrl}/api/jobs/${key}/filesets/${jobFileSetKey}/progress`, {
        method: 'PATCH',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            etag: eTag,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ TotalSizeUploaded: segmentSizeUploaded, isCompleted: isCompleted })
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.headers.get('etag') ?? '';
};

export const searchJobsList = async (options: JobListRequest) => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const headers = {
        Authorization: `Bearer ${accessToken}`
    };

    const requestOptions = {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ ...options })
    };

    const response = await fetchData(`${PortalBaseUrl}/api/jobs/search`, requestOptions, true);

    if (!response.ok) {
        return Promise.reject(response);
    }
    const result = await response.json();

    return result;
};

export const deleteJob = async (key: string): Promise<void> => {
    const accessToken = await getAccessToken();

    const response = await fetchData(`${PortalBaseUrl}/api/jobs/${key}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return Promise.resolve();
};

